/**
 * AI Settings - 核心业务逻辑
 */

import { ref, computed } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { getAIOutputLanguage, saveAIOutputLanguage } from '../api';
import {
    SUPPORTED_LANGUAGES,
    getDefaultLanguageByRegion,
    getLanguageByCode,
    getAILabelByCode,
    STORAGE_KEY,
} from '../constants';
import type { LanguageOption } from '../types';

/**
 * 从 cookie 中读取界面语言，并映射到 AI 输出语言格式
 */
function getUILanguageFromCookie(): string {
    const pattern = /---uniform-setting-locale=(\w+)/;
    const match = document.cookie.match(pattern);

    console.log('[useAISettings] Reading UI language from cookie:', {
        cookie: document.cookie,
        match: match,
    });

    if (!match) return '';

    const uiLang = match[1];

    // 映射：界面语言代码 -> AI 输出语言代码
    const langMap: Record<string, string> = {
        'cn': 'zh-CN',
        'en': 'en',
        'tw': 'zh-TW',
        'jp': 'ja',
    };

    const mappedLang = langMap[uiLang] || '';
    console.log('[useAISettings] Mapped UI language:', { uiLang, mappedLang });

    return mappedLang;
}

/**
 * AI设置管理
 *
 * 功能：
 * - 从后端加载用户设置
 * - 本地缓存加速读取
 * - 保存设置到后端
 * - 提供当前语言的AI label
 */
export function useAISettings() {
    // 本地缓存：加速读取，避免每次都请求后端
    const cachedLanguage = useLocalStorage<string>(STORAGE_KEY, '');

    // 当前选中的语言code
    const currentLanguage = ref<string>(cachedLanguage.value || getDefaultLanguageByRegion());

    // 加载状态
    const loading = ref(false);

    // 保存状态
    const saving = ref(false);

    // 错误信息
    const error = ref<string>('');

    /**
   * 当前语言配置对象
   */
    const currentLanguageOption = computed<LanguageOption | undefined>(() => {
        return getLanguageByCode(currentLanguage.value);
    });

    /**
   * 发送给AI的语言描述
   *
   * 🔥 直接使用 AI Settings 中的设置（currentLanguage）
   * 这样用户在 AI Settings 中设置的语言会被正确使用
   */
    const aiLanguageLabel = computed<string>(() => {
        const label = getAILabelByCode(currentLanguage.value);
        console.log('[useAISettings] Computing aiLanguageLabel:', {
            currentLanguage: currentLanguage.value,
            cachedLanguage: cachedLanguage.value,
            label,
        });
        return label;
    });

    /**
   * 是否已加载
   */
    const isLoaded = ref(false);

    /**
   * 从后端加载设置
   */
    async function loadSettings() {
        if (loading.value) return;

        loading.value = true;
        error.value = '';

        try {
            const data = await getAIOutputLanguage();

            // 更新当前语言
            currentLanguage.value = data.language;

            // 更新本地缓存
            cachedLanguage.value = data.language;

            isLoaded.value = true;
        } catch (err: any) {
            // 检查是否是404错误（API未实现）
            const is404 = err?.response?.status === 404 || err?.status === 404 || err?.message?.includes('404');

            if (is404) {
                console.warn('[AI Settings] API not implemented yet, using default language');
            } else {
                console.error('Failed to load AI settings:', err);
                error.value = err.message || 'Failed to load settings';
            }

            // 加载失败时使用默认值
            const defaultLang = getDefaultLanguageByRegion();
            currentLanguage.value = defaultLang;
            cachedLanguage.value = defaultLang;
        } finally {
            loading.value = false;
        }
    }

    /**
   * 保存设置到后端
   */
    async function saveSettings(languageCode: string) {
        if (saving.value) return 'saving';

        saving.value = true;
        error.value = '';

        try {
            const data = await saveAIOutputLanguage(languageCode);

            // 更新当前语言
            currentLanguage.value = data.language;

            // 更新本地缓存
            cachedLanguage.value = data.language;

            return 'success';
        } catch (err: any) {
            // 检查是否是404错误（API未实现）
            const is404 = err?.response?.status === 404 || err?.status === 404 || err?.message?.includes('404');

            if (is404) {
                console.warn('[AI Settings] Save API not implemented yet, using local storage only');
                // API未实现时，至少保存到本地
                currentLanguage.value = languageCode;
                cachedLanguage.value = languageCode;
                return 'silent'; // 返回silent，表示静默保存，不显示成功提示
            } else {
                console.error('Failed to save AI settings:', err);
                error.value = err.message || 'Failed to save settings';
                return 'error';
            }
        } finally {
            saving.value = false;
        }
    }

    /**
   * 更新语言（保存到后端）
   */
    async function updateLanguage(languageCode: string) {
        return await saveSettings(languageCode);
    }

    return {
    // 状态
        currentLanguage,
        currentLanguageOption,
        aiLanguageLabel,
        loading,
        saving,
        error,
        isLoaded,

        // 常量
        supportedLanguages: SUPPORTED_LANGUAGES,

        // 方法
        loadSettings,
        saveSettings,
        updateLanguage,
    };
}
